import { h, mount } from "@/lib/dom";
import {
  loading,
  emptyState,
  button,
  badge,
  openModal,
  field,
  textInput,
  select,
  toast,
} from "@/components/ui";
import {
  subjectsService,
  topicsService,
  testsService,
  timetableService,
  holidaysService,
  findHoliday,
  nextSummerBreakEnd,
  pbErrorMessage,
  isSonstigesSubject,
  type ExamSuggestion,
  type Holiday,
} from "@/services";
import { navigate } from "@/app/router";
import { EXAM_TYPE_OPTIONS, examTypeLabel } from "@/features/topics/examFields";
import { examTermField } from "@/features/topics/examTermField";
import { openTopicSettings } from "@/features/topics/topicSettings";
import { openCsvImportDialog } from "@/features/exams/csvImport";
import { offlineSync } from "@/services/offline/offline-sync.service";
import { checkConnectivity, connectivityMessage } from "@/services/offline/connectivity";
import { voiceControl } from "@/features/voice/voicePanel";
import { formatDate, formatCountdown, daysUntil } from "@/lib/format";
import { addDays, lessonsUntil, toLocalISODate, upcomingSubjectSlots } from "@/features/timetable/schedule";
import type { ExamType, Subject, Topic, Test, Period, TimetableSlot, Weekday } from "@/types/models";

/** Der Lernplan liegt vorerst brach – die Bausteine bleiben für später stehen. */
const SHOW_STUDY_PLAN = false;

interface ExamRow {
  kind: "exam";
  date: string;
  topic: Topic;
  subject?: Subject;
}

interface TestRow {
  kind: "test";
  date: string;
  test: Test;
  topic: Topic;
  subject?: Subject;
}

type Row = ExamRow | TestRow;

/** Prüfungstermine aller Themen – ein Termin pro Thema (Felder am Thema). */
export function ExamsPage(): HTMLElement {
  const container = h("div", null);
  let initialized = false;
  // Vergangene Termine sind normalerweise weg; die Uhr holt sie zurück.
  let showPast = false;
  let subjects: Subject[] = [];
  let topics: Topic[] = [];
  let tests: Test[] = [];
  let ttSlots: TimetableSlot[] = [];
  let ttPeriods: Period[] = [];
  let ttDays: Weekday[] = [];
  let holidays: Holiday[] = [];

  async function load() {
    if (!initialized) mount(container, loading());
    try {
      const now = new Date();
      const [subs, tops, tsts, slots, settings, hols] = await Promise.all([
        subjectsService.listReal().catch(() => [] as Subject[]),
        topicsService.listAll().catch(() => [] as Topic[]),
        testsService.listAll().catch(() => [] as Test[]),
        timetableService.listSlots().catch(() => [] as TimetableSlot[]),
        timetableService.getSettings().catch(() => null),
        holidaysService.forRange(now, addDays(now, 400)).catch(() => [] as Holiday[]),
      ]);
      subjects = subs;
      topics = tops;
      tests = tsts;
      ttSlots = slots;
      ttPeriods = settings?.periods ?? [];
      ttDays = settings?.days ?? [];
      holidays = hols;
      initialized = true;
      render();
    } catch (err) {
      initialized = true;
      const [cachedSubjectsRaw, cachedTopics] = await Promise.all([offlineSync.loadSubjects(), offlineSync.loadTopics()]);
      const cachedSubjects = cachedSubjectsRaw?.filter((s) => !isSonstigesSubject(s));
      if (cachedSubjects && cachedTopics) {
        subjects = cachedSubjects;
        topics = cachedTopics;
        tests = [];
        ttSlots = [];
        ttPeriods = [];
        ttDays = [];
        holidays = [];
        render();
        toast("Offline – zeige zwischengespeicherten Stand.", "warning");
        return;
      }
      const status = await checkConnectivity();
      mount(
        container,
        h(
          "div",
          { class: "card card-pad" },
          emptyState({
            icon: "calendar",
            title: "Konnte nicht laden",
            text: status.backendReachable ? pbErrorMessage(err) : connectivityMessage(status),
            action: button({ label: "Erneut versuchen", onClick: () => void load() }),
          })
        )
      );
    }
  }
  const refresh = () => void load();

  /* ---------- Mikro-Erfassung ---------- */

  let voicePanelEl: HTMLElement | null = null;

  interface Candidate {
    date: string;
    periodFrom: number;
    periodTo: number;
  }
  /** Zuletzt berechnete Kandidaten je Fach – von `buildVoiceContext` befüllt, von `resolveCandidateIndex` gelesen. */
  let subjectCandidates: Record<string, Candidate[]> = {};

  /**
   * Je Fach ein paar echte, ferienfreie Termine aus dem Stundenplan – exakt
   * dieselbe Quelle wie `examTermField`s Dropdown. Ohne das hatte die KI kein
   * heutiges Datum und keinen Stundenplan zur Hand und erfand Termine (auch
   * mit falschem Jahr).
   */
  function buildVoiceContext(): unknown {
    const today = new Date();
    const boundary = nextSummerBreakEnd(holidays, today) ?? addDays(today, 84);
    const weeksAhead = Math.max(1, Math.ceil((boundary.getTime() - today.getTime()) / (7 * 86_400_000)));

    const candidates: Record<string, Candidate[]> = {};
    for (const s of subjects) {
      const upcoming = upcomingSubjectSlots({
        slots: ttSlots,
        periods: ttPeriods,
        days: ttDays,
        subjectId: s.id,
        from: today,
        weeksAhead,
      })
        .filter((slot) => slot.date.getTime() <= boundary.getTime() && !findHoliday(holidays, toLocalISODate(slot.date)))
        .slice(0, 6)
        .map((slot) => ({ date: toLocalISODate(slot.date), periodFrom: slot.from, periodTo: slot.to }));
      if (upcoming.length) candidates[s.id] = upcoming;
    }
    subjectCandidates = candidates;

    return {
      today: toLocalISODate(today),
      subjects: subjects.map((s) => ({ id: s.id, title: s.title })),
      topics: topics.map((t) => ({ id: t.id, title: t.title, subjectId: t.subject, state: t.state })),
      // Je Kandidat sein Index mitgeben: die KI soll nur diese Zahl
      // zurückgeben (candidateIndex), nicht selbst mit Daten rechnen.
      candidates: Object.fromEntries(
        Object.entries(candidates).map(([id, list]) => [id, list.map((c, i) => ({ index: i, ...c }))])
      ),
    };
  }

  /**
   * Löst `candidateIndex` (falls die KI eine Stunde aus der Liste gewählt
   * hat) in echtes Datum + Stunde auf – bevor der Vorschlag angezeigt wird.
   * Ohne Treffer (Index unbekannt/Kandidatenliste inzwischen anders) bleibt
   * der Vorschlag, wie er ist.
   */
  function resolveCandidateIndex(p: ExamSuggestion): ExamSuggestion {
    if (p.candidateIndex == null) return p;
    const candidate = subjectCandidates[p.subjectId]?.[p.candidateIndex];
    if (!candidate) return p;
    return { ...p, date: candidate.date, periodFrom: candidate.periodFrom, periodTo: candidate.periodTo, candidateIndex: undefined };
  }

  async function applyVoiceExam(p: ExamSuggestion): Promise<void> {
    if (!p.subjectId || !subjects.some((s) => s.id === p.subjectId)) throw new Error("Unbekanntes Fach – bitte Fach wählen.");
    if (!p.date) throw new Error("Bitte ein Datum wählen.");
    let topicId = p.topicId;
    if (!topicId) {
      const created = await topicsService.create({
        subject: p.subjectId,
        title: p.topicTitle?.trim() || "Prüfung",
        state: "active",
      });
      topicId = created.id;
    }
    await topicsService.setExam(topicId, {
      date: p.date,
      examType: (p.examType as ExamType) || "sonstiges",
      periodFrom: p.periodFrom,
      periodTo: p.periodTo,
    });
  }

  function voiceExamPanel(): HTMLElement {
    voicePanelEl ??= voiceControl<ExamSuggestion>({
      domain: "exam",
      buildContext: buildVoiceContext,
      applyOne: applyVoiceExam,
      normalize: resolveCandidateIndex,
      onApplied: refresh,
      columns: [
        {
          label: "Fach",
          render: (p, patch) =>
            select({
              value: p.subjectId,
              options: subjects.map((s) => ({ value: s.id, label: s.title })),
              onchange: (e) => {
                const id = (e.target as HTMLSelectElement).value;
                patch({ subjectId: id, subjectTitle: subjects.find((s) => s.id === id)?.title });
              },
            }),
        },
        {
          label: "Thema",
          render: (p, patch) => {
            if (p.topicId) {
              const t = topics.find((x) => x.id === p.topicId);
              return h("span", { class: "muted text-sm" }, t?.title ?? "(bestehendes Thema)");
            }
            const el = textInput({ value: p.topicTitle ?? "", placeholder: "Neues Thema" });
            el.addEventListener("input", () => patch({ topicTitle: el.value }));
            return el;
          },
        },
        {
          label: "Termin",
          render: (p, patch) => {
            const el = h("input", { type: "date", class: "input", value: p.date ?? "" }) as HTMLInputElement;
            el.addEventListener("input", () => patch({ date: el.value }));
            return el;
          },
        },
        {
          label: "Form",
          render: (p, patch) =>
            select({
              value: p.examType,
              options: EXAM_TYPE_OPTIONS,
              onchange: (e) => patch({ examType: (e.target as HTMLSelectElement).value }),
            }),
        },
      ],
    });
    return voicePanelEl;
  }

  function render() {
    const subjectById = new Map(subjects.map((subject) => [subject.id, subject]));
    const topicById = new Map(topics.map((topic) => [topic.id, topic]));
    // Älteste zuerst: vergangene Termine stehen im Stift-Modus ganz oben,
    // danach folgt chronologisch, was noch kommt. Klausuren (Feld am Thema)
    // und kleinere Tests (eigene Collection) laufen in derselben Liste.
    const examRows: Row[] = topics
      .filter((topic) => topic.examDate && topic.state !== "archived")
      .map((topic) => ({ kind: "exam", date: topic.examDate!, topic, subject: subjectById.get(topic.subject) }));
    const testRows: Row[] = tests
      .map((test) => ({ test, topic: topicById.get(test.topic) }))
      .filter((r): r is { test: Test; topic: Topic } => !!r.topic && r.topic.state !== "archived")
      .map((r) => ({ kind: "test", date: r.test.date, test: r.test, topic: r.topic, subject: subjectById.get(r.topic.subject) }));
    const all: Row[] = [...examRows, ...testRows].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    const rows = showPast ? all : all.filter((r) => (daysUntil(r.date) ?? 0) >= 0);
    const pastCount = all.length - all.filter((r) => (daysUntil(r.date) ?? 0) >= 0).length;

    const header = h(
      "div",
      { class: "page-header" },
      h("h1", null, "Prüfungen"),
      h("span", { class: "spacer" }),
      pastToggle(pastCount),
      voiceExamPanel(),
      button({
        icon: "upload",
        variant: "ghost",
        disabled: subjects.length === 0,
        title: subjects.length === 0 ? "Lege zuerst ein Fach an" : "CSV-Import",
        onClick: () => openCsvImportDialog({ subjects, topics, onImported: refresh }),
      }),
      button({
        label: "Test",
        icon: "plus",
        variant: "ghost",
        size: "sm",
        disabled: topics.length === 0,
        title: topics.length === 0 ? "Lege zuerst ein Thema an" : "Kleineren Test zu einem bestehenden Thema hinzufügen",
        onClick: () => openQuickTestForm(topics, subjects, refresh),
      }),
      button({
        label: "Neuer Termin",
        icon: "plus",
        variant: "primary",
        disabled: subjects.length === 0,
        title: subjects.length === 0 ? "Lege zuerst ein Fach an" : undefined,
        onClick: () => openQuickExamTopicForm(subjects, topics, refresh),
      })
    );

    const body = rows.length === 0 ? emptyCard() : monthGroupedList(rows);

    const nextRow = rows.find((r) => {
      const d = daysUntil(r.date);
      return d !== null && d >= 0 && d <= 30;
    });
    const plan = SHOW_STUDY_PLAN && nextRow ? renderStudyPlan(nextRow.topic) : null;

    mount(container, h("div", null, header, plan, body));
  }

  function emptyCard(): HTMLElement {
    return h(
      "div",
      { class: "card card-pad" },
      emptyState({
        icon: "calendar",
        title: "Keine anstehenden Prüfungen",
        text:
          subjects.length === 0
            ? "Lege zuerst ein Fach an, dann kannst du hier Termine erfassen."
            : "Lege einen Termin an oder trage ihn in den Thema-Einstellungen ein.",
        action:
          subjects.length > 0
            ? button({
                label: "Termin anlegen",
                icon: "plus",
                variant: "primary",
                onClick: () => openQuickExamTopicForm(subjects, topics, refresh),
              })
            : button({
                label: "Zu den Fächern",
                icon: "chevron-left",
                onClick: () => navigate("/"),
              }),
      })
    );
  }

  /** Kategorie ist immer der Kalendermonat des Termins (chronologisch, wie sortiert). */
  function monthGroupedList(rows: Row[]): HTMLElement {
    const groups: { label: string; rows: Row[] }[] = [];
    let lastKey = "";
    for (const row of rows) {
      const d = new Date(row.date);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (key !== lastKey) {
        const label = d.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
        groups.push({ label: label.charAt(0).toUpperCase() + label.slice(1), rows: [] });
        lastKey = key;
      }
      groups[groups.length - 1].rows.push(row);
    }
    return h(
      "div",
      { class: "col" },
      ...groups.flatMap((g) => [
        h("h2", { class: "subject-category" }, g.label),
        ...g.rows.map((r) => (r.kind === "exam" ? examRow(r) : testRow(r))),
      ])
    );
  }

  /** Uhr statt Stift: zeigt zusätzlich, was schon vorbei ist. */
  function pastToggle(pastCount: number): HTMLElement {
    const btn = button({
      icon: "clock",
      variant: "ghost",
      title: showPast
        ? "Vergangene Termine ausblenden"
        : pastCount
          ? `${pastCount} vergangene Termine anzeigen`
          : "Keine vergangenen Termine",
      disabled: !showPast && pastCount === 0,
      onClick: () => {
        showPast = !showPast;
        render();
      },
    });
    btn.classList.add("edit-btn");
    btn.classList.toggle("active", showPast);
    return btn;
  }

  /**
   * Eine Zeile zeigt Fach, Prüfungsform und Termin – das Thema steht bewusst
   * nicht dabei, es hängt ohnehin am Fach. Der Klick führt direkt in die
   * Einstellungen des Themas, wo Termin und Form geändert werden.
   */
  function lessonsLeft(subjectId: string | undefined, date: string, period?: number): HTMLElement | null {
    if (!subjectId || !ttSlots.length || (daysUntil(date) ?? -1) < 0) return null;
    const n = lessonsUntil({
      slots: ttSlots,
      periods: ttPeriods,
      days: ttDays,
      subjectId,
      until: date.slice(0, 10),
      untilPeriod: period || undefined,
      isFree: (iso) => !!findHoliday(holidays, iso),
    });
    return h("span", { class: "exam-lessons-left" }, n === 1 ? "noch 1 Unterrichtsstunde" : `noch ${n} Unterrichtsstunden`);
  }

  function examRow({ topic, subject }: ExamRow): HTMLElement {
    const days = daysUntil(topic.examDate!);
    const kind = days === null ? "" : days < 0 ? "archived" : days <= 7 ? "upcoming" : "active";
    const period =
      topic.examPeriodFrom
        ? topic.examPeriodTo && topic.examPeriodTo !== topic.examPeriodFrom
          ? ` · ${topic.examPeriodFrom}.–${topic.examPeriodTo}. Std.`
          : ` · ${topic.examPeriodFrom}. Std.`
        : "";

    return h(
      "button",
      {
        class: "card card-pad exam-topic-row",
        type: "button",
        title: `${topic.title} – Einstellungen öffnen`,
        onclick: () => void openTopicSettings({ topic, onChanged: refresh, showTopicLink: true }),
      },
      badge(formatCountdown(topic.examDate!), kind),
      h(
        "div",
        { class: "exam-topic-main" },
        h("div", { class: "exam-topic-title" }, subject?.title ?? "Unbekanntes Fach")
      ),
      h(
        "div",
        { class: "exam-topic-side muted text-sm" },
        h("span", { class: "exam-topic-form" }, examTypeLabel(topic.examType) || "Prüfung"),
        h("span", null, formatDate(topic.examDate!) + period),
        lessonsLeft(subject?.id, topic.examDate!, topic.examPeriodFrom)
      )
    );
  }

  /** Wie `examRow`, nur für einen kleineren Test – Klick öffnet Bearbeiten/Löschen statt der Themen-Einstellungen. */
  function testRow({ test, topic, subject }: TestRow): HTMLElement {
    const days = daysUntil(test.date);
    const kind = days === null ? "" : days < 0 ? "archived" : days <= 7 ? "upcoming" : "active";
    const period =
      test.periodFrom
        ? test.periodTo && test.periodTo !== test.periodFrom
          ? ` · ${test.periodFrom}.–${test.periodTo}. Std.`
          : ` · ${test.periodFrom}. Std.`
        : "";

    return h(
      "button",
      {
        class: "card card-pad exam-topic-row",
        type: "button",
        title: `${test.title || "Test"} (${topic.title}) – bearbeiten`,
        onclick: () => openEditTestDialog(test, topic, refresh),
      },
      badge(formatCountdown(test.date), kind),
      h(
        "div",
        { class: "exam-topic-main" },
        h("div", { class: "exam-topic-title" }, subject?.title ?? "Unbekanntes Fach")
      ),
      h(
        "div",
        { class: "exam-topic-side muted text-sm" },
        h("span", { class: "exam-topic-form" }, test.title || "Test"),
        h("span", null, formatDate(test.date) + period),
        lessonsLeft(subject?.id, test.date, test.periodFrom)
      )
    );
  }

  function openQuickTestForm(topics: Topic[], subjects: Subject[], onSaved: () => void): void {
    const openTopics = topics.filter((t) => t.state !== "archived");
    if (openTopics.length === 0) {
      toast("Lege zuerst ein Thema an.", "error");
      return;
    }
    const subjectById = new Map(subjects.map((s) => [s.id, s]));

    const topicSelect = select({
      options: openTopics.map((t) => ({
        value: t.id,
        label: `${subjectById.get(t.subject)?.title ?? "—"} · ${t.title}`,
      })),
      value: openTopics[0].id,
      onchange: () => {
        const subjectId = openTopics.find((t) => t.id === topicSelect.value)?.subject;
        if (subjectId) termField.setSubject(subjectId);
      },
    });
    const titleInput = textInput({ placeholder: "z. B. Vokabeltest Kapitel 3 (optional)" });
    const termField = examTermField({ subjectId: openTopics[0].subject });
    const errorBox = h("div", { class: "field-error" });
    const saveBtn = button({ label: "Speichern", variant: "primary", onClick: () => void save() });

    async function save() {
      const term = termField.getValue();
      if (!term.date) {
        errorBox.textContent = "Bitte ein Datum wählen.";
        return;
      }
      saveBtn.disabled = true;
      errorBox.textContent = "";
      try {
        await testsService.create({
          topic: topicSelect.value,
          title: titleInput.value.trim() || undefined,
          date: term.date,
          periodFrom: term.periodFrom,
          periodTo: term.periodTo,
        });
        toast("Test gespeichert.", "success");
        handle.close();
        onSaved();
      } catch (err) {
        errorBox.textContent = pbErrorMessage(err);
        saveBtn.disabled = false;
      }
    }

    const handle = openModal({
      title: "Neuer Test",
      body: h(
        "div",
        null,
        field("Thema", topicSelect),
        field("Titel", titleInput),
        field("Termin", termField.el),
        errorBox
      ),
      footer: h(
        "div",
        { class: "row" },
        button({ label: "Abbrechen", variant: "ghost", onClick: () => handle.close() }),
        saveBtn
      ),
    });
  }

  function openEditTestDialog(test: Test, topic: Topic, onSaved: () => void): void {
    const titleInput = textInput({ value: test.title ?? "", placeholder: "z. B. Vokabeltest Kapitel 3 (optional)" });
    const termField = examTermField({
      subjectId: topic.subject,
      value: { date: test.date, periodFrom: test.periodFrom, periodTo: test.periodTo },
    });
    const errorBox = h("div", { class: "field-error" });
    const saveBtn = button({ label: "Speichern", variant: "primary", onClick: () => void save() });

    async function save() {
      const term = termField.getValue();
      if (!term.date) {
        errorBox.textContent = "Bitte ein Datum wählen.";
        return;
      }
      saveBtn.disabled = true;
      errorBox.textContent = "";
      try {
        await testsService.update(test.id, {
          title: titleInput.value.trim() || undefined,
          date: term.date,
          periodFrom: term.periodFrom,
          periodTo: term.periodTo,
        });
        toast("Test gespeichert.", "success");
        handle.close();
        onSaved();
      } catch (err) {
        errorBox.textContent = pbErrorMessage(err);
        saveBtn.disabled = false;
      }
    }

    async function remove() {
      try {
        await testsService.remove(test.id);
        toast("Test gelöscht.", "success");
        handle.close();
        onSaved();
      } catch (err) {
        errorBox.textContent = pbErrorMessage(err);
      }
    }

    const handle = openModal({
      title: `Test – ${topic.title}`,
      body: h("div", null, field("Titel", titleInput), field("Termin", termField.el), errorBox),
      footer: h(
        "div",
        { class: "row" },
        button({ label: "Löschen", variant: "ghost", onClick: () => void remove() }),
        h("span", { class: "spacer" }),
        button({ label: "Abbrechen", variant: "ghost", onClick: () => handle.close() }),
        saveBtn
      ),
    });
  }

  function openQuickExamTopicForm(subjects: Subject[], topics: Topic[], onSaved: () => void): void {
    if (subjects.length === 0) {
      toast("Lege zuerst ein Fach an.", "error");
      return;
    }
    const subjectById = new Map(subjects.map((s) => [s.id, s]));
    const topicById = new Map(topics.map((t) => [t.id, t]));
    const openTopics = topics.filter((t) => !t.examDate && t.state !== "archived");

    const topicSelect = select({
      options: [
        { value: "", label: "➕ Neues Thema anlegen" },
        ...openTopics.map((t) => ({
          value: t.id,
          label: `${subjectById.get(t.subject)?.title ?? "—"} · ${t.title}`,
        })),
      ],
      value: "",
      onchange: () => updateMode(),
    });

    const subjectSelect = select({
      options: subjects.map((subject) => ({ value: subject.id, label: subject.title })),
      value: subjects[0]?.id,
      onchange: () => {
        if (topicSelect.value === "") termField.setSubject(subjectSelect.value);
      },
    });
    const titleInput = textInput({ placeholder: "z. B. Analysis Klausur" });
    const newFields = h("div", null, field("Fach", subjectSelect), field("Titel", titleInput));

    const termField = examTermField({ subjectId: subjectSelect.value });
    const typeSelect = select({ options: EXAM_TYPE_OPTIONS, value: "klassenarbeit" });
    const errorBox = h("div", { class: "field-error" });
    const saveBtn = button({ label: "Speichern", variant: "primary", onClick: () => void save() });

    function updateMode() {
      const isNew = topicSelect.value === "";
      newFields.style.display = isNew ? "" : "none";
      const subjectId = isNew ? subjectSelect.value : topicById.get(topicSelect.value)?.subject;
      if (subjectId) termField.setSubject(subjectId);
    }
    updateMode();

    async function save() {
      const isNew = topicSelect.value === "";
      const term = termField.getValue();
      if (!term.date) {
        errorBox.textContent = "Bitte ein Datum wählen.";
        return;
      }
      if (isNew && !titleInput.value.trim()) {
        errorBox.textContent = "Bitte einen Titel eingeben.";
        return;
      }
      saveBtn.disabled = true;
      errorBox.textContent = "";
      try {
        const topicId = isNew
          ? (
              await topicsService.create({
                subject: subjectSelect.value,
                title: titleInput.value.trim(),
                state: "active",
              })
            ).id
          : topicSelect.value;
        await topicsService.setExam(topicId, {
          date: term.date,
          examType: typeSelect.value as ExamType,
          periodFrom: term.periodFrom,
          periodTo: term.periodTo,
        });
        toast("Prüfungstermin gespeichert.", "success");
        handle.close();
        onSaved();
      } catch (err) {
        errorBox.textContent = pbErrorMessage(err);
        saveBtn.disabled = false;
      }
    }

    const handle = openModal({
      title: "Neuer Prüfungstermin",
      body: h(
        "div",
        null,
        field("Thema", topicSelect),
        newFields,
        field("Termin", termField.el),
        field("Prüfungsform", typeSelect),
        errorBox
      ),
      footer: h(
        "div",
        { class: "row" },
        button({ label: "Abbrechen", variant: "ghost", onClick: () => handle.close() }),
        saveBtn
      ),
    });
  }

  void load();
  return container;
}

/* ---------- Lernplan (derzeit ausgeblendet, siehe SHOW_STUDY_PLAN) ---------- */

interface PlanTask { id: string; method: string; label: string; note: string; }
interface PlanWeek { label: string; phase: string; tasks: PlanTask[]; exam?: boolean; }

// Lern-Methoden in pädagogisch sinnvoller Reihenfolge (Aufbau → Vertiefen →
// Festigen → Prüfungsnah). „method" verlinkt direkt in die Üben-Seite.
const PLAN_LIB: Record<string, { label: string; note: string }> = {
  sq3r:     { label: "SQ4R-Lesen",        note: "Stoff strukturiert erschließen" },
  feynman:  { label: "Feynman-Methode",   note: "in eigenen Worten erklären – deckt Lücken auf" },
  karten:   { label: "Karteikarten",      note: "Spaced Repetition – das Rückgrat, oft kurz wiederholen" },
  warum:    { label: "Warum-Fragen",      note: "Zusammenhänge & Ursachen verstehen" },
  vergleich:{ label: "Vergleichen",       note: "Konzepte gegenüberstellen" },
  bruecken: { label: "Eselsbrücken",      note: "schwierige Begriffe verankern" },
  quiz:     { label: "Quiz (gemischt)",   note: "Abruf testen, sofortiges Feedback" },
  luecken:  { label: "Lückentext-Sprint", note: "Fachbegriffe gezielt abrufen" },
  zuordnen: { label: "Zuordnen & Sortieren", note: "Begriffe vernetzen" },
  recall:   { label: "Active Recall",     note: "frei abrufen, dann vergleichen" },
  blitz:    { label: "Blitzrunde",        note: "schnelles Abrufen automatisieren" },
  fehler:   { label: "Fehlersuche",       note: "tiefes Prüfen statt Wiedererkennen" },
  klausur:  { label: "Probeklausur",      note: "unter Prüfungsbedingungen üben" },
  pruefung: { label: "Prüfungssimulation",note: "voller Durchlauf mit Benotung" },
};

function planDoneKey(topicId: string) { return `plan_done_${topicId}`; }
function loadDone(topicId: string): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(planDoneKey(topicId)) ?? "[]") as string[]); }
  catch { return new Set(); }
}
function saveDone(topicId: string, done: Set<string>): void {
  try { localStorage.setItem(planDoneKey(topicId), JSON.stringify([...done])); } catch { /* ignore */ }
}

/**
 * Baut einen wochengenauen Plan: jede Methode kommt (außer Karteikarten als
 * Spacing-Anker) genau einmal vor, von Aufbau bis prüfungsnah.
 */
function buildWeeks(days: number): PlanWeek[] {
  if (days <= 0) return [{ label: "Prüfungstag", phase: "Heute", exam: true, tasks: [] }];
  const weeksLeft = Math.max(1, Math.ceil(days / 7));

  const mk = (week: number, method: string): PlanTask => ({
    id: `w${week}-${method}`, method, label: PLAN_LIB[method].label, note: PLAN_LIB[method].note,
  });

  // Phasen-Pool (ohne Karteikarten – die kommen als Anker in jede Woche).
  const phases: { phase: string; methods: string[] }[] = [
    { phase: "Aufbau – verstehen",   methods: ["sq3r", "feynman"] },
    { phase: "Vertiefen – verknüpfen", methods: ["warum", "vergleich", "bruecken"] },
    { phase: "Festigen – abrufen",   methods: ["quiz", "luecken", "zuordnen", "recall", "blitz"] },
    { phase: "Prüfungsnah – anwenden", methods: ["klausur", "fehler", "pruefung"] },
  ];

  // Wenig Zeit (≤ 1 Woche): ein verdichteter Endspurt-Plan in „Tagesblöcken".
  if (weeksLeft === 1) {
    const blocks: PlanWeek[] = [
      { label: `Heute–morgen (${days} Tg.)`, phase: "Verstehen & anlegen",
        tasks: [mk(1, "sq3r"), mk(1, "feynman"), mk(1, "karten")] },
      { label: "Mitte der Woche", phase: "Abrufen & festigen",
        tasks: [mk(2, "quiz"), mk(2, "luecken"), mk(2, "recall")] },
      { label: "Letzte 2 Tage", phase: "Prüfungsnah",
        tasks: [mk(3, "klausur"), mk(3, "fehler"), mk(3, "karten")] },
      { label: "Prüfungstag", phase: "Heute", exam: true, tasks: [] },
    ];
    return blocks;
  }

  // Mehrere Wochen: Phasen über die Wochen verteilen, je Woche Karteikarten-Anker.
  const display = Math.min(weeksLeft, 5);
  const weeks: PlanWeek[] = [];
  for (let w = 0; w < display; w++) {
    const isLast = w === display - 1;
    const remainingWeeks = weeksLeft - w;
    const label = w === 0 ? "Diese Woche"
      : isLast ? "Letzte Woche vor der Prüfung"
      : `Woche ${w + 1} (noch ${remainingWeeks} Wo.)`;
    // Phase nach Position wählen: früh Aufbau, spät Prüfungsnah
    const phaseIdx = isLast ? 3 : Math.min(2, Math.floor((w / Math.max(1, display - 1)) * 3));
    const phase = phases[phaseIdx];
    const picks = phase.methods.slice(0, isLast ? 3 : 2).map((m) => mk(w + 1, m));
    picks.push(mk(w + 1, "karten")); // Spacing-Anker jede Woche
    weeks.push({ label, phase: phase.phase, tasks: picks });
  }
  weeks.push({ label: "Prüfungstag", phase: "Heute", exam: true, tasks: [] });
  return weeks;
}

/** Lernplan-Karte: manuell ausklappbar; Methoden klickbar; Erledigt-Haken. */
function renderStudyPlan(topic: Topic): HTMLElement {
  const days = daysUntil(topic.examDate ?? "") ?? 0;
  const done = loadDone(topic.id);

  const body = h("div", { class: "study-plan-body", style: "display:none" });
  let built = false;

  function buildBody() {
    const weeks = buildWeeks(days);
    const totalTasks = weeks.reduce((n, w) => n + w.tasks.length, 0);

    const progress = h("div", { class: "plan-progress muted text-sm" });
    function updateProgress() {
      const doneCount = weeks.reduce((n, w) => n + w.tasks.filter((t) => done.has(t.id)).length, 0);
      progress.textContent = `${doneCount} / ${totalTasks} Aufgaben erledigt`;
    }

    const timeline = h("div", { class: "study-plan-timeline" },
      ...weeks.map((week) =>
        h("div", { class: "study-plan-day" + (week.exam ? " plan-exam" : "") },
          h("div", { class: "study-plan-label" }, week.label),
          h("div", { class: "plan-phase" }, week.phase),
          week.exam
            ? h("ul", { class: "study-plan-tasks" },
                h("li", null, "Nur kurz überfliegen, tief durchatmen – du hast geübt! 🍀"))
            : h("div", { class: "plan-tasks" },
                ...week.tasks.map((t) => {
                  const check = h("input", { type: "checkbox", class: "plan-check" }) as HTMLInputElement;
                  check.checked = done.has(t.id);
                  check.addEventListener("change", () => {
                    if (check.checked) done.add(t.id); else done.delete(t.id);
                    saveDone(topic.id, done);
                    row.classList.toggle("done", check.checked);
                    updateProgress();
                  });
                  const row = h("div", { class: "plan-task" + (done.has(t.id) ? " done" : "") },
                    check,
                    h("button", { class: "plan-task-go", type: "button",
                      title: `„${t.label}" für dieses Thema starten`,
                      onclick: () => navigate(`/topic/${topic.id}/learn/${t.method}`) },
                      h("span", { class: "plan-task-label" }, t.label),
                      h("span", { class: "plan-task-note muted text-sm" }, t.note)
                    )
                  );
                  return row;
                })
              )
        )
      )
    );

    updateProgress();
    mount(body, h("div", null,
      h("p", { class: "muted text-sm", style: "margin-bottom:10px" },
        "Pädagogisch aufgebaut: erst verstehen, dann verknüpfen, festigen und prüfungsnah anwenden. " +
        "Karteikarten begleiten jede Woche (Spacing). Klicke eine Aufgabe, um sie direkt zu starten, und hake sie danach ab."),
      progress,
      timeline
    ));
  }

  const toggleBtn = button({
    label: "Lernplan erstellen", icon: "calendar", variant: "primary", size: "sm",
    onClick: () => {
      const open = body.style.display === "none";
      body.style.display = open ? "" : "none";
      if (open && !built) { buildBody(); built = true; }
    },
  });

  return h("div", { class: "card card-pad study-plan", style: "margin-bottom:14px" },
    h("div", { class: "row", style: "align-items:center" },
      h("div", { class: "section-title", style: "margin:0" },
        `📅 Lernplan für „${topic.title}"`),
      h("span", { class: "muted text-sm", style: "margin-left:8px" }, `noch ${days} Tag${days === 1 ? "" : "e"}`),
      h("span", { class: "spacer" }),
      toggleBtn
    ),
    body
  );
}
